import { BaseUserProfile } from '@/types/BaseUser';

/**
 * User - Complete user profile interface
 * 
 * Extends BaseUserProfile with additional fields specific to the full user profile.
 * This is used throughout the application for authenticated users.
 * 
 * @see BaseUserProfile - For shared fields with LeaderboardUser
 */
export interface User extends BaseUserProfile {
  // Authentication & Identity
  handle: string;
  email: string;
  
  // Extended Profile fields (not in BaseUserProfile)
  customTimerUnlocked: boolean;
  coins: number;
  rank: number | null;
  hasActivity?: boolean;
  percentile?: number | null;
  
  // Extended Study statistics
  totalStudyHours: number;
  totalSessions: number;
  studyDays: number;
  currentStreak: number;
  longestStreak: number;
  
  // Daily & session data
  todayStudyTime: number;
  todaySessionCount: number;
  weeklyStudyTime: number;
  
  // Session timing
  lastStudyDate: string;
  lastSessionEndTime: string;
  
  // Achievements & cosmetics
  achievements: string[];
  stars: number;
  
  // Avatar customization
  avatarBackground: string;
  avatarFrame: string;
  avatarColor: string;
  avatarConfig: {
    outfitId?: string;
    accessoryId?: string;
    backgroundId?: string;
    effectId?: string;
  };
  
  // Goals
  daily_goal_minutes: number;
  daily_goal_last_changed?: string;
  daily_goal_changes_count?: number;
  dailyGoal: number;
  goalHours: number;
  
  // Handle change tracking
  handle_changes_count?: number;
  handle_last_changed?: string;
  
  // Location & Profile
  timezone: string;
  following: string[];
  selected_profile_stats?: string[];
  show_city_on_profile?: boolean;
  
  // Professional and Solo mode
  use_initials_avatar?: boolean;
  study_profile?: 'solo' | 'social';
  
  // Prestige system
  prestigeCount?: number;
  
  // Email notifications
  email_notifications?: boolean;
  preferred_reminder_time?: string;
  
  // Break statistics
  break_compliance_rate?: number;
  avg_break_duration?: number;
  breaks_skipped_count?: number;
  forge_heat?: number;
  custom_forge_cycle?: {
    study: number;
    break: number;
    preset?: string;
  } | null;
  custom_forge_cycles?: Array<{
    name: string;
    study: number;
    break: number;
  }> | null;
  
  // Streak protection (Premium feature)
  streak_freeze_days_remaining?: number;
  streak_freeze_days_reset_date?: string;
  streak_freeze_days_used_this_period?: number;
  
  // Sound effects
  sound_effects_enabled?: boolean;
  
  // Theme preference (synced across devices)
  theme?: string;
  
  // Admin status (server-side verified via has_role RPC)
  isAdmin?: boolean;
  
  // Tutorial tracking
  first_study_done?: boolean;
  
  // First-time tooltip progress
  tutorial_progress?: Record<string, boolean>;
}

export interface BreakData {
  sessionId: string;
  recommendedMinutes: number;
  actualMinutes: number;
  completed: boolean;
  activities: string[];
  coinsEarned: number;
}

export interface UserContextType {
  user: User;
  updateUser: (updates: Partial<User>) => Promise<void>;
  // NOTE: awardCoins REMOVED - all coin operations use atomic RPCs:
  // - Session coins: validate-study-session → increment_user_coins RPC
  // - Break coins: updateBreakStats → modify_user_coins RPC  
  // - Shop purchases: Shop.tsx → modify_user_coins RPC
  updateStreak: () => void;
  canStartSession: () => { canStart: boolean; reason?: string };
  addStudySession: (minutes: number, silent?: boolean) => Promise<void>;
  resetWeeklyStats: () => void;
  updateBreakStats: (breakData: BreakData) => Promise<void>;
  loading: boolean;
  // V2: Timeout handling for graceful recovery
  profileTimedOut: boolean;
  retryProfileLoad: () => void;
}

// Default user data
export const defaultUser: User = {
  id: "user-default-id",
  handle: "",
  username: "StudyForge User",
  email: "user@studyforge.com",
  avatar: "https://api.dicebear.com/7.x/pixel-art/svg?seed=default-seed",
  avatar_seed: "default-seed",
  isPremium: false,
  customTimerUnlocked: false,
  prestigeTitle: "Apprentice I",
  level: 1,
  coins: 100,
  rank: null,
  hasActivity: false,
  percentile: null,
  totalStudyHours: 0,
  totalSessions: 0,
  studyDays: 0,
  currentStreak: 0,
  longestStreak: 0,
  streak: 0,
  current_streak: 0,
  longest_streak: 0,
  study_days: 0,
  total_sessions: 0,
  timeSpent: 0,
  sessions_today: 0,
  weekly_time: 0,
  todayStudyTime: 0,
  todaySessionCount: 0,
  weeklyStudyTime: 0,
  lastStudyDate: "",
  lastSessionEndTime: "",
  achievements: [],
  stars: 0,
  avatarBackground: "#6366f1",
  avatarFrame: "basic",
  avatarColor: "#ffffff",
  avatarConfig: {},
  daily_goal_minutes: 120,
  dailyGoal: 120,
  goalHours: 1000,
  quote: "",
  country: "",
  state: "",
  city: "",
  timezone: "America/Chicago",
  following: [],
  sound_effects_enabled: true,
  theme: 'default',
  first_study_done: false,
  tutorial_progress: {},
  study_profile: 'solo',
  forge_heat: 0
};
